import { Children } from 'react';
import PropTypes from 'prop-types';
import Component, { debug } from '@ux/component';

/**
 * Provide additional namespacing logic on top of @ux/component. Useful for
 * custom styling. Used by eldorado headers for example.
 *
 * @displayName NamespaceConsumer
 *
 * @Constructor
 * @class NamespaceComponent
 * @public
 */
export class NamespaceConsumer extends Component {
  get namespace() {
    return (this.context || {}).namespace || ((...c) => c.filter(Boolean).join(' '));
  }
}

/**
 * Can be extended on by children of the NamespaceComponent to receive context.
 *
 * @type {Object}
 * @public
 */
NamespaceConsumer.contextTypes = {
  namespace: PropTypes.func,
  ...Component.contextTypes
};

/**
 * Provide additional namespacing logic on top of @ux/component. Useful for
 * custom styling. Used by eldorado headers for example.
 *
 * @displayName NamespaceProvider
 *
 * @Constructor
 * @class NamespaceComponent
 * @public
 */
export class NamespaceProvider extends Component {
  constructor(props = {}, ...args) {
    super(props, ...args);

    if (props.namespace) {
      this._namespace = props.namespace;
    }
  }

  /**
   * Return context for children.
   *
   * @returns {Object} React context
   * @private
   */
  getChildContext() {
    debug('Retrieving context');

    return {
      namespace: this.namespace
    };
  }

  /**
   * Return namespaced value. Prefer context over the regular function,
   * context will be properly scoped and has access to `_namespace`.
   *
   * @returns {String} namespace.
   * @public
   */
  get namespace() {
    return (...str) => {
      str = str.filter(Boolean);

      if (!this._namespace) {
        return str.join(' ');
      }

      if (!str.length) {
        return;
      }

      //
      // Handle the case where a single string is provided with multiple entries.
      //
      if (str.length === 1 && ~str[0].indexOf(' ')) {
        str = str[0].split(' ');
      }

      return str
        .map(s => `${this._namespace}-${s}`)
        .join(' ');
    };
  }

  /**
   * Set namespace for local.
   *
   * @param {String} name Namespace.
   * @public
   */
  set namespace(name) {
    debug('Setting namespace to %s', name);

    this._namespace = name;
  }

  /**
   * Wrap a single component to provide the namespace through context.
   *
   * @returns {ReactElement} Single React element.
   * @private
   */
  render() {
    return Children.only(this.props.children);
  }
}

/**
 * Define properties.
 *
 * @type {Object}
 * @public
 */
NamespaceProvider.propTypes = {
  /** The namespace to use */
  namespace: PropTypes.string,
  /** Child components */
  children: PropTypes.node
};

/**
 * Define context.
 *
 * @type {Object}
 * @public
 */
NamespaceProvider.childContextTypes = {
  namespace: PropTypes.func
};
